import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/frontend/components/ui/dialog";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Label } from "@/frontend/components/ui/label";
import { Textarea } from "@/frontend/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/frontend/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/frontend/components/ui/popover";
import { Calendar } from "@/frontend/components/ui/calendar";
import { ToggleGroup, ToggleGroupItem } from "@/frontend/components/ui/toggle-group";
import { CalendarIcon, Loader2, AlertTriangle, Ban, UserPlus } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/frontend/lib/utils";
import { toast as sonnerToast } from "sonner";
import { Booking, GuestInfo } from "@/shared/types";
import { useAdminResidences } from "@/backend/hooks/admin/useAdminResidences";
import { useAdminRoomTypes } from "@/backend/hooks/admin/useAdminRoomTypes";
import {
  useCreateAdminBlock,
  useCreateAdminOfflineBooking,
  useUpdateAdminBooking,
  StudentCredential,
} from "@/backend/hooks/admin/useAdminBookings";
import { useRoomTypeOccupancy, findFirstAvailableUnit } from "@/backend/hooks/useRoomTypeOccupancy";
import { getRoomLabel } from "@/backend/lib/roomUtils";
import { sendBookingApprovedEmail } from "@/backend/services/email/emailClient";
import { StudentCredentialsModal } from "@/frontend/components/admin/StudentCredentialsModal";
import { GuestFields } from "@/frontend/components/booking/GuestFields";

type Mode = "block" | "booking";

export interface AdminBookingPrefill {
  residenceId?: string;
  roomTypeId?: string;
  checkIn?: Date;
  checkOut?: Date;
}

interface CreateAdminBookingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prefill?: AdminBookingPrefill;
  /** When provided, the dialog edits this existing admin entry instead of creating. */
  editEntry?: Booking | null;
}

const emptyGuest = (): GuestInfo => ({ name: "", email: "", phone: "" });

export const CreateAdminBookingDialog = ({
  open,
  onOpenChange,
  prefill,
  editEntry,
}: CreateAdminBookingDialogProps) => {
  const { data: residences = [] } = useAdminResidences();
  const { data: roomTypes = [] } = useAdminRoomTypes();
  const createBlock = useCreateAdminBlock();
  const createOfflineBooking = useCreateAdminOfflineBooking();
  const updateAdminBooking = useUpdateAdminBooking();

  const isEditing = !!editEntry;

  const [mode, setMode] = useState<Mode>("block");
  const [residenceId, setResidenceId] = useState("");
  const [roomTypeId, setRoomTypeId] = useState("");
  const [checkIn, setCheckIn] = useState<Date | undefined>();
  const [checkOut, setCheckOut] = useState<Date | undefined>();
  const [numberOfOccupants, setNumberOfOccupants] = useState(1);
  const [guests, setGuests] = useState<GuestInfo[]>([emptyGuest()]);
  const [adminNote, setAdminNote] = useState("");

  const [credentials, setCredentials] = useState<StudentCredential[] | null>(null);
  const [credentialsOpen, setCredentialsOpen] = useState(false);
  // True for the whole submit (incl. the slow account-creation + emails) so the
  // occupancy warning doesn't flash once the new booking saves its own dates.
  const [submitting, setSubmitting] = useState(false);

  // Reset the form each time the dialog opens — from the edited entry if any,
  // otherwise from the prefill.
  useEffect(() => {
    if (!open) return;
    if (editEntry) {
      setMode(editEntry.status === "blocked" ? "block" : "booking");
      setResidenceId(editEntry.residenceId);
      setRoomTypeId(editEntry.roomTypeId ?? "");
      setCheckIn(editEntry.checkIn.toDate());
      setCheckOut(editEntry.checkOut.toDate());
      setNumberOfOccupants(editEntry.numberOfOccupants || 1);
      setGuests(editEntry.guests.length ? editEntry.guests : [emptyGuest()]);
      setAdminNote(editEntry.adminNote ?? "");
      return;
    }
    setMode("block");
    setResidenceId(prefill?.residenceId ?? "");
    setRoomTypeId(prefill?.roomTypeId ?? "");
    setCheckIn(prefill?.checkIn);
    setCheckOut(prefill?.checkOut);
    setNumberOfOccupants(1);
    setGuests([emptyGuest()]);
    setAdminNote("");
    setSubmitting(false);
  }, [open, prefill, editEntry]);

  const units = roomTypes
    .filter((rt) => rt.residenceId === residenceId)
    .sort((a, b) => (a.createdAt?.toMillis?.() ?? 0) - (b.createdAt?.toMillis?.() ?? 0));
  const selectedUnit = roomTypes.find((rt) => rt.id === roomTypeId);
  const maxOccupancy = selectedUnit?.maxOccupancy ?? 1;

  // Availability guard: is the chosen unit free for the chosen range? Skipped
  // when editing — the entry occupies its own range, so it always self-conflicts.
  const { data: occupancy } = useRoomTypeOccupancy(roomTypeId ? [roomTypeId] : undefined);
  const hasConflict =
    !isEditing &&
    !!roomTypeId &&
    !!checkIn &&
    !!checkOut &&
    checkOut > checkIn &&
    !!occupancy &&
    findFirstAvailableUnit([roomTypeId], occupancy.unitRanges, checkIn, checkOut) === null;

  // Occupied ranges for the chosen unit — used to grey out taken dates in the
  // pickers, mirroring the public booking calendar. Skipped while editing (the
  // entry occupies its own range, which would block its own dates).
  const unitRanges = (roomTypeId && occupancy?.unitRanges[roomTypeId]) || [];
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const isCheckInDisabled = (date: Date) => {
    if (date < startOfToday) return true;
    if (isEditing) return false;
    // A day is taken if it falls within an occupied range [start, end).
    return unitRanges.some((r) => r.start <= date && date < r.end);
  };

  const isCheckOutDisabled = (date: Date) => {
    if (!checkIn || date <= checkIn) return true;
    if (isEditing) return false;
    // Disallow a checkout that would make [checkIn, date) overlap an occupied range.
    return unitRanges.some((r) => checkIn < r.end && date > r.start);
  };

  // Same visual treatment as the public booking calendar: occupied days stay
  // visible but struck through, so it's obvious why they can't be picked.
  const occupiedModifiers = isEditing
    ? undefined
    : { occupied: unitRanges.map((r) => ({ from: r.start, to: r.end })) };
  const occupiedModifiersClassNames = { occupied: "opacity-40 line-through" };

  const updateGuest = (index: number, field: keyof GuestInfo, value: string) => {
    setGuests((prev) => {
      const next = [...prev];
      // Cast covers the union-typed fields (documentType, occupation).
      next[index] = { ...next[index], [field]: value } as GuestInfo;
      return next;
    });
  };

  const handleOccupantsChange = (count: number) => {
    setNumberOfOccupants(count);
    setGuests((prev) => {
      const next = [...prev];
      while (next.length < count) next.push(emptyGuest());
      next.length = count;
      return next;
    });
  };

  const datesValid = !!checkIn && !!checkOut && checkOut > checkIn;
  // Guest details aren't edited in edit mode, so they don't gate saving.
  const guestsValid =
    isEditing ||
    mode === "block" ||
    guests.every(
      (g) => g.name.trim() && g.email.trim() && g.phone.trim()
    );
  const canSubmit =
    !!residenceId && !!roomTypeId && datesValid && guestsValid && !hasConflict;

  const isPending =
    createBlock.isPending || createOfflineBooking.isPending || updateAdminBooking.isPending;

  const handleSubmit = async () => {
    if (!canSubmit || !checkIn || !checkOut) return;
    setSubmitting(true);
    try {
      if (isEditing && editEntry) {
        await updateAdminBooking.mutateAsync({
          id: editEntry.id,
          residenceId,
          roomTypeId,
          checkIn,
          checkOut,
          adminNote,
        });
        sonnerToast.success(mode === "block" ? "Block updated" : "Booking updated");
        onOpenChange(false);
        return;
      }
      if (mode === "block") {
        await createBlock.mutateAsync({
          residenceId,
          roomTypeId,
          checkIn,
          checkOut,
          adminNote,
        });
        sonnerToast.success("Availability block created");
        onOpenChange(false);
      } else {
        const result = await createOfflineBooking.mutateAsync({
          residenceId,
          roomTypeId,
          checkIn,
          checkOut,
          numberOfOccupants,
          guests,
          adminNote,
        });

        // Email each guest their credentials + stay summary, like the approval flow.
        const residenceName = residences.find((r) => r.id === residenceId)?.name || "Unknown";
        const roomTypeName = getRoomLabel(roomTypeId, roomTypes).name;
        const portalUrl = `${window.location.origin}/student/login`;
        const checkInStr = format(checkIn, "MMMM d, yyyy");
        const checkOutStr = format(checkOut, "MMMM d, yyyy");

        const emailResults = await Promise.all(
          result.credentials.map((cred) =>
            sendBookingApprovedEmail({
              to: cred.email,
              guestName: cred.name,
              loginEmail: cred.email,
              tempPassword: cred.password,
              residenceName,
              roomTypeName,
              checkIn: checkInStr,
              checkOut: checkOutStr,
              portalUrl,
            }).then((r) => ({ email: cred.email, success: r.success }))
          )
        );
        const failed = emailResults.filter((r) => !r.success).map((r) => r.email);

        if (failed.length === 0) {
          sonnerToast.success("Offline booking created", {
            description: "Credentials emailed to the guest(s).",
          });
        } else {
          sonnerToast.error("Booking created · some emails failed", {
            description: `Failed: ${failed.join(", ")}. Use the credentials below to share manually.`,
          });
        }

        setCredentials(result.credentials);
        setCredentialsOpen(true);
        onOpenChange(false);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create entry";
      sonnerToast.error("Could not create entry", { description: message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isEditing
                ? mode === "block"
                  ? "Edit block"
                  : "Edit booking"
                : "New booking / block"}
            </DialogTitle>
            <DialogDescription>
              {isEditing
                ? "Update the unit, dates or note. Guest details and student accounts are not changed here."
                : "Take a unit offline or record a booking made offline. Both apply to a single unit and update public availability immediately."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Mode toggle — hidden when editing (kind is fixed) */}
            {!isEditing && (
            <ToggleGroup
              type="single"
              value={mode}
              onValueChange={(v) => v && setMode(v as Mode)}
              className="grid grid-cols-2 gap-2"
            >
              <ToggleGroupItem
                value="block"
                variant="outline"
                className="h-11 data-[state=on]:bg-slate-700 data-[state=on]:text-white data-[state=on]:border-slate-700 data-[state=on]:hover:bg-slate-700 data-[state=on]:hover:text-white"
              >
                <Ban className="h-4 w-4 mr-2" />
                Block
              </ToggleGroupItem>
              <ToggleGroupItem
                value="booking"
                variant="outline"
                className="h-11 data-[state=on]:bg-emerald-600 data-[state=on]:text-white data-[state=on]:border-emerald-600 data-[state=on]:hover:bg-emerald-600 data-[state=on]:hover:text-white"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Offline booking
              </ToggleGroupItem>
            </ToggleGroup>
            )}

            {/* Residence + unit */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Residence</Label>
                <Select
                  value={residenceId}
                  onValueChange={(v) => {
                    setResidenceId(v);
                    setRoomTypeId("");
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select residence" />
                  </SelectTrigger>
                  <SelectContent>
                    {residences.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Unit</Label>
                <Select value={roomTypeId} onValueChange={setRoomTypeId} disabled={!residenceId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map((u) => {
                      const label = getRoomLabel(u.id, roomTypes);
                      return (
                        <SelectItem key={u.id} value={u.id}>
                          {label.name}
                          {label.typologyKey
                            ? ` · ${label.typologyKey}${label.unitNumber ? ` #${label.unitNumber}` : ""}`
                            : ""}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Check-in</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !checkIn && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {checkIn ? format(checkIn, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={checkIn}
                      onSelect={(d) => {
                        setCheckIn(d);
                        if (d && checkOut && checkOut <= d) setCheckOut(undefined);
                      }}
                      disabled={isCheckInDisabled}
                      modifiers={occupiedModifiers}
                      modifiersClassNames={occupiedModifiersClassNames}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Check-out</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      disabled={!checkIn}
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !checkOut && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {checkOut ? format(checkOut, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={checkOut}
                      defaultMonth={checkIn}
                      onSelect={setCheckOut}
                      disabled={isCheckOutDisabled}
                      modifiers={occupiedModifiers}
                      modifiersClassNames={occupiedModifiersClassNames}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {hasConflict && !submitting && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" aria-hidden="true" />
                <p>This unit is already occupied for the selected dates. Pick a different unit or range.</p>
              </div>
            )}

            {/* Block: reason */}
            {mode === "block" && (
              <div className="space-y-2">
                <Label htmlFor="admin-note">
                  Reason <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <Textarea
                  id="admin-note"
                  placeholder="e.g. Maintenance, occupied via another channel…"
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  rows={2}
                />
              </div>
            )}

            {/* Offline booking: occupancy + guests + note */}
            {mode === "booking" && (
              <div className="space-y-4">
                {!isEditing && (
                <>
                <div className="space-y-2">
                  <Label>Number of occupants</Label>
                  <Select
                    value={String(numberOfOccupants)}
                    onValueChange={(v) => handleOccupantsChange(Number(v))}
                    disabled={!selectedUnit}
                  >
                    <SelectTrigger className="w-full sm:w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: maxOccupancy }, (_, i) => i + 1).map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {guests.map((guest, i) => (
                  <div key={i} className="space-y-3 rounded-lg border p-3">
                    {guests.length > 1 && (
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Student {i + 1}
                      </p>
                    )}
                    <GuestFields
                      guest={guest}
                      index={i}
                      onUpdate={(field, value) => updateGuest(i, field, value)}
                      showEmailNotice={false}
                    />
                  </div>
                ))}
                </>
                )}

                <div className="space-y-2">
                  <Label htmlFor="admin-note-booking">
                    Internal note <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Textarea
                    id="admin-note-booking"
                    placeholder="e.g. Phone booking – João"
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    rows={2}
                  />
                </div>

                {!isEditing && (
                  <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 mt-0.5" aria-hidden="true" />
                    <p>
                      Creating this booking immediately creates a student account per guest and
                      emails each guest their login credentials. The credentials are also shown
                      after creation so you can share them manually if an email fails.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit || isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing
                ? "Save changes"
                : mode === "block"
                ? "Create block"
                : "Create booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {credentials && (
        <StudentCredentialsModal
          open={credentialsOpen}
          onClose={() => {
            setCredentialsOpen(false);
            setCredentials(null);
          }}
          students={credentials}
        />
      )}
    </>
  );
};
